// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll, { FancyScrollRefProps } from "@awc/App/Components/Common/FancyScroll";
import FloatingMenuTooltip from "@awc/App/Components/Menu/FloatingMenuTooltip";
import { xHeaderUpdate } from "@awc/App/Handlers/XNG/Header";
import { CustomAppState } from "@awc/App/Store/Reducers";
import TextField from "@awc/MobiusOverrides/TextField";
import { SearchInputStyles } from "@awc/Overrides/Widgets/Header/SearchInput";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteProducts from "@insite/content-library/Widgets/Header/AutocompleteProducts";
import AutocompleteSuggestions from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import SearchHistory from "@insite/content-library/Widgets/Header/SearchHistory";
import { searchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { PositionStyle } from "@insite/mobius/Popover";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    xHeaderUpdate,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    clickedProduct: boolean;
    inputWidth?: number;
    autoCompleteOpen?: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

const styles = searchInputStyles;
const StyledForm = getStyledWrapper("form");

class PunchoutSearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    private autocompleteColumnProductParentMenu = React.createRef<HTMLUListElement>();
    private autocompleteColumnCategoryParentMenu = React.createRef<HTMLUListElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private fancyScrollRef = createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);
        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);
        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
            autoCompleteOpen: false,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                this.setState({ query: "" });
                this.showAutocomplete = true;
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
        this.setState({ inputWidth: this.inputWrapperRef.current?.getBoundingClientRect().width });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }
        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            // XNG-Changes
            this.goToUrl(this.state.autoCompleteModel?.products[0].url, true, 1);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.setState({ query: "" });
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: (result: any) => {
                if (this.showAutocomplete) {
                    this.setState({ autoCompleteModel: result }, () => {
                        this.fancyScrollRef?.current?.updateScrollable();
                    });
                    this.updatePopoverVisibility(result);
                }
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        if (newQuery) {
            this.loadAutocomplete(newQuery);
        }
        this.updatePopoverVisibility(this.state.autoCompleteModel);
        if (newQuery === "") {
            this.clearSearchQuery();
        }
    };

    openAutocomplete = () => {
        this.setState({ autoCompleteOpen: true });
    };

    closeAutocomplete = () => {
        this.setState({ autoCompleteOpen: false });
    };

    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    updatePopoverVisibility(results: AutocompleteModel | undefined) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    (event.target as any).blur();
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    (event.target as any).blur();
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    (event.target as any).blur();
                    this.doSearch();
                }

                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }
        this.setState({
            clickedProduct: false,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true, autoCompleteResultLength?: number) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        // only when clicking/hitting enter on pdp product from search result
        //  (having 1 product search result only) remove stale autocomplete results
        if (autoCompleteResultLength && autoCompleteResultLength === 1) {
            this.setState({
                // don't use stale results on next search and go to wrong PDP
                autoCompleteModel: undefined,
            });
        }
        this.props.history.push(url);
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return null;
        }
        const {
            categories,
            brands,
            content,
            products,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;
        const hasProducts = products && products.length > 0;
        const hasContent =
            (categories && categories.length > 0) || (brands && brands.length > 0) || (content && content.length > 0);

        return (
            <>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                <AutocompleteCategories
                                    categories={categories}
                                    popularCategories={popularCategories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}

                {!isRetailSearchCompletionResults && (
                    <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                        {((categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)) && (
                            <FancyScroll
                                refElement={this.autocompleteColumnCategoryParentMenu}
                                ref={this.fancyScrollRef}
                            >
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnWrapper}
                                    className="autocomplete-column-wrapper"
                                    ref={this.autocompleteColumnCategoryParentMenu as any}
                                >
                                    <AutocompleteCategories
                                        categories={categories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    />
                                    <AutocompleteBrands
                                        brands={brands}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteBrandsStyles}
                                    />
                                    <AutocompleteContent
                                        content={content}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteContentStyles}
                                    />
                                </StyledWrapper>
                            </FancyScroll>
                        )}
                        {hasProducts && (
                            <FancyScroll
                                refElement={this.autocompleteColumnProductParentMenu}
                                ref={this.fancyScrollRef}
                            >
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnProductWrapper}
                                    className="autocomplete-column-wrapper"
                                    ref={this.autocompleteColumnProductParentMenu as any}
                                    {...{ hasContent }}
                                >
                                    <AutocompleteProducts
                                        products={products}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteProductsStyles}
                                        {...{ doSearch: this.doSearch }}
                                    />
                                </StyledWrapper>
                            </FancyScroll>
                        )}
                    </StyledWrapper>
                )}
            </>
        );
    };

    renderSearchAutoCompleteContent = () => {
        const searchHistoryItems = getSearchHistory();
        const hasSearchHistory = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        if (!this.state.query && this.showAutocomplete && hasSearchHistory) {
            return (
                <StyledWrapper
                    {...this.styles.searchPopUpWrapper}
                    {...{ width: this.state.inputWidth ? this.state.inputWidth - 2 : 300 }}
                >
                    <SearchHistory focusedQuery={this.state.focusedSearchHistoryQuery} goToUrl={this.goToUrl} />
                </StyledWrapper>
            );
        }
        if (this.state.query) {
            return this.renderAutocomplete();
        }
        return null;
    };

    handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        this.doSearch();
    };

    render() {
        const styles = this.styles;
        const searchId = `punchoutHeaderSearch-${this.props.id}`;
        const searchInput = (
            <StyledForm id="punchout-search-form" onSubmit={this.handleSubmit} noValidate>
                <TextField
                    data-test-selector="punchoutHeaderSearchInputTextField"
                    className="search-input"
                    {...styles.input}
                    id={searchId}
                    type="search"
                    value={this.state.query}
                    onChange={e => this.handleInputChange(e)}
                    onFocus={this.handleInputFocus}
                    onKeyDown={this.handlePopoverKeyDown}
                    autoComplete="off"
                    iconClickableProps={{
                        className: "searchClickable",
                        onClick: this.doSearch,
                    }}
                />
            </StyledForm>
        );
        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <FloatingMenuTooltip
                    trigger="manual"
                    isOpen={this.state.autoCompleteOpen}
                    triggerComponent={searchInput}
                    showArrow={false}
                    placement="bottom-start"
                    offset={0}
                    extendedStyles={styles.floatingTooltip}
                    onOpenChange={open => {
                        this.setState({ autoCompleteOpen: open });
                        this.props.xHeaderUpdate({ key: "mobileSearch", value: open } as any);
                    }}
                >
                    {this.renderSearchAutoCompleteContent()}
                </FloatingMenuTooltip>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(PunchoutSearchInput)));
